import './App.css';
import React from 'react';
import LoginPage from './components/LoginPage';
import SignupPage from './components/SignupPage';
import ParentDashboard from './components/ParentDashboard';
import KidDashboard from './components/KidDashboard';
import AdminDashboard from './components/AdminDashboard';
import NotificationBanner from './components/NotificationBanner';
import './assets/styles/LoginPage.css';
import { saveToken, removeToken, getToken, saveUser, getUser, removeUser } from './utils/auth';
import { useSSE } from './hooks/useSSE';
import { initGA, logPageView } from './utils/analytics';

import LandingPage from './components/LandingPage';
import Blog from './components/Blog';
import PrivacyPolicy from './components/PrivacyPolicy';
import axios from './config/axios';

const App = () => {
  const [view, setView] = React.useState('landing');
  const [user, setUser] = React.useState(null);

  // SSE hook for real-time notifications
  const { isConnected, lastNotification, clearNotification } = useSSE();

  // Initialize analytics on mount
  React.useEffect(() => {
    initGA();
  }, []);

  // Track page views when view changes
  React.useEffect(() => {
    logPageView(view);
  }, [view]);

  // Check for existing session on mount
  React.useEffect(() => {
    const restoreSession = async () => {
      const token = getToken();
      const savedUser = getUser();

      if (token && savedUser) {
        console.log('Restoring session for user:', savedUser.username);
        setUser(savedUser);

        // Default to parent dashboard for admins, only show admin dashboard if explicitly on /admin
        if (window.location.pathname === '/admin' && savedUser.role === 'admin') {
          setView('admin-dashboard');
        } else if (savedUser.role === 'admin' || savedUser.role === 'parent') {
          setView('parent-dashboard');
        } else {
          setView('kid-dashboard');
        }
      } else if (savedUser) {
        // We have savedUser but no in-memory token, try to restore session using HTTP-only cookie
        try {
          console.log('Restoring session via cookie/auth-me...');
          const response = await axios.get('/api/v1/auth/me');
          if (response.data && response.data.user) {
            const userObj = {
              ...response.data.user,
              user_id: response.data.user.id || response.data.user.ID,
              tenant_id: response.data.user.TenantID || response.data.user.tenant_id,
              display_name: response.data.user.display_name || response.data.user.DisplayName
            };
            console.log('Session restored successfully via auth/me:', userObj.username);
            saveUser(userObj);
            setUser(userObj);
            if (window.location.pathname === '/admin' && userObj.role === 'admin') {
              setView('admin-dashboard');
            } else if (userObj.role === 'admin' || userObj.role === 'parent') {
              setView('parent-dashboard');
            } else {
              setView('kid-dashboard');
            }
          }
        } catch (err) {
          console.error('Failed to restore session via cookie:', err);
          handleLogout();
        }
      } else if (window.location.pathname === '/admin') {
        // Redirect to login if trying to access admin without session
        setView('login');
      }
    };

    restoreSession();
  }, []);

  // Handle URL routing for static pages
  React.useEffect(() => {
    const path = window.location.pathname;
    if (path === '/privacy') {
      setView('privacy');
    }
  }, []);

  const handleLoginSuccess = (userData) => {
    // Save JWT token and user data to localStorage
    if (userData.token) {
      saveToken(userData.token);
      console.log('Token saved to localStorage');
    }
    saveUser(userData);
    console.log('User data saved to localStorage');

    setUser(userData);
    // Admins go to parent dashboard by default, admin panel only if they were trying to go there
    if (window.location.pathname === '/admin' && userData.role === 'admin') {
      setView('admin-dashboard');
    } else if (userData.role === 'admin' || userData.role === 'parent') {
      setView('parent-dashboard');
    } else {
      setView('kid-dashboard');
    }
  };

  const handleLogout = () => {
    // Remove token and user data from localStorage
    removeToken();
    removeUser();
    setUser(null);
    setView('landing');
  };

  return (
    <div className="App">
      {/* Real-time notification banner */}
      {user && lastNotification && (
        <NotificationBanner
          notification={lastNotification}
          onDismiss={clearNotification}
        />
      )}

      {/* SSE connection status indicator (only in development) */}
      {process.env.NODE_ENV === 'development' && user && (
        <div className="sse-status">
          <span className={`sse-status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
          <span>{isConnected ? 'Live' : 'Offline'}</span>
        </div>
      )}

      {view === 'landing' && (
        <LandingPage
          onLogin={() => setView('login')}
          onKidLogin={() => setView('kid-login')}
          onSignup={() => setView('signup')}
          onBlog={() => setView('blog')}
          onPrivacy={() => setView('privacy')}
        />
      )}
      {view === 'privacy' && (
        <PrivacyPolicy />
      )}
      {view === 'blog' && (
        <Blog
          onBack={() => setView('landing')}
          onSignup={() => setView('signup')}
        />
      )}
      {view === 'login' && (
        <LoginPage
          onBack={() => setView('landing')}
          onSignupLink={() => setView('signup')}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      {view === 'kid-login' && (
        <LoginPage
          onBack={() => setView('landing')}
          onSignupLink={() => setView('signup')}
          onLoginSuccess={handleLoginSuccess}
          showKidLoginFlow={true}
        />
      )}
      {view === 'signup' && (
        <SignupPage
          onBack={() => setView('landing')}
          onLoginLink={() => setView('login')}
        />
      )}
      {view === 'parent-dashboard' && (
        <ParentDashboard user={user} onLogout={handleLogout} />
      )}
      {view === 'kid-dashboard' && (
        <KidDashboard user={user} onLogout={handleLogout} />
      )}
      {view === 'admin-dashboard' && (
        <AdminDashboard user={user} onLogout={handleLogout} />
      )}
    </div>
  );
};

export default App;

